import type {
    PatientDetailDto,
    StructureSummaryDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import { toRecordedDatasetOrigin } from '~/domain/adapters/toRecordedDatasetOrigin';
import type {
    ConnectorDoseManifestEntry,
    ConnectorImageManifestEntry,
    ConnectorPatientManifest,
    ConnectorRegistrationManifestEntry,
    ConnectorStructureSetManifestEntry,
} from '~/domain/persistence/ConnectorPatientManifest';

/** ROI numbers of the structures matching `predicate`; rows without a number are skipped. */
function roiNumbersWhere(
    structures: readonly StructureSummaryDto[] | null | undefined,
    predicate: (structure: StructureSummaryDto) => boolean
): number[] {
    return (structures ?? [])
        .filter(predicate)
        .map((structure) => structure.roiNumber)
        .filter((roiNumber): roiNumber is number => roiNumber !== undefined);
}

/**
 * Builds the persistence manifest from the patient detail the open flow
 * already fetched. This is the DTO → transport-shape boundary: every wire
 * field is optional, so anything without a usable identity is dropped
 * rather than fabricated.
 *
 * Returns null when the patient itself lacks id/PatientID/version (an older
 * backend without per-series versions, or a malformed response) — the open
 * still works, the session is just "local only" and cannot save.
 */
export function buildConnectorManifest(
    detail: PatientDetailDto
): ConnectorPatientManifest | null {
    const { id, dicomPatientId, version } = detail;
    if (!id || !dicomPatientId || !version) {
        return null;
    }

    const structureSets: ConnectorStructureSetManifestEntry[] = [];
    const registrations: ConnectorRegistrationManifestEntry[] = [];
    const images: ConnectorImageManifestEntry[] = [];
    const doses: ConnectorDoseManifestEntry[] = [];
    for (const study of detail.studies ?? []) {
        for (const series of study.series ?? []) {
            // A dataset missing its row id, series identity, or load-time
            // version token cannot participate in the save workflow; skip
            // it so it hydrates as "local only".
            if (!series.id || !series.seriesInstanceUid || !series.version) {
                continue;
            }
            switch (series.datasetType) {
                case 'StructureSet':
                    structureSets.push({
                        datasetId: series.id,
                        seriesInstanceUid: series.seriesInstanceUid,
                        sopInstanceUid: series.sopInstanceUid ?? null,
                        version: series.version,
                        approvalStatus:
                            series.structureSet?.approvalStatus ?? null,
                        reviewerName: series.structureSet?.reviewerName ?? null,
                        lockedRoiNumbers: roiNumbersWhere(
                            series.structureSet?.structures,
                            (structure) => structure.locked === true
                        ),
                        seriesDescription: series.seriesDescription ?? null,
                        origin: toRecordedDatasetOrigin(
                            series.structureSet?.origin
                        ),
                        modelGeneratedRoiNumbers: roiNumbersWhere(
                            series.structureSet?.structures,
                            (structure) => structure.modelGenerated === true
                        ),
                        contourEditedRoiNumbers: roiNumbersWhere(
                            series.structureSet?.structures,
                            (structure) =>
                                structure.contourEditedSinceGeneration === true
                        ),
                    });
                    break;
                case 'Registration':
                    registrations.push({
                        datasetId: series.id,
                        seriesInstanceUid: series.seriesInstanceUid,
                        sopInstanceUid: series.sopInstanceUid ?? null,
                        version: series.version,
                        approvalStatus:
                            series.registration?.approvalStatus ?? null,
                        reviewerName: series.registration?.reviewerName ?? null,
                        seriesDescription: series.seriesDescription ?? null,
                    });
                    break;
                case 'Image3D':
                    images.push({
                        datasetId: series.id,
                        seriesInstanceUid: series.seriesInstanceUid,
                        version: series.version,
                        seriesDescription: series.seriesDescription ?? null,
                    });
                    break;
                case 'Dose':
                    doses.push({
                        datasetId: series.id,
                        seriesInstanceUid: series.seriesInstanceUid,
                        sopInstanceUid: series.sopInstanceUid ?? null,
                        version: series.version,
                        seriesDescription: series.seriesDescription ?? null,
                    });
                    break;
                default:
                    // Plans and unknown kinds are read-only in the viewer.
                    break;
            }
        }
    }

    return {
        patientId: id,
        dicomPatientId,
        version,
        structureSets,
        registrations,
        images,
        doses,
    };
}
